import React, { useState, useEffect } from 'react';
import { getMonedaById, createMoneda, updateMoneda } from '../services/monedaService';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, X, ArrowLeft, Coins, RefreshCw } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

export default function MonedaForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { token } = useAuth();
    const isEditMode = !!id;

    const [formData, setFormData] = useState({
        nombre: '',
        codigoIso: '',
        simbolo: ''
    });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isEditMode) {
            cargarMoneda();
        }
    }, [id]);

    const cargarMoneda = async () => {
        try {
            setLoading(true);
            const data = await getMonedaById(token, id);
            setFormData({
                nombre: data.nombre,
                codigoIso: data.codigoIso,
                simbolo: data.simbolo
            });
        } catch (error) {
            console.error('Error al cargar la moneda:', error);
            toast.error('Error al cargar los datos de la moneda');
            navigate('/parametrizacion/monedas');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.nombre.trim() || !formData.codigoIso.trim() || !formData.simbolo.trim()) {
            toast.error('Todos los campos son obligatorios');
            return;
        }

        try {
            setLoading(true);
            if (isEditMode) {
                await updateMoneda(token, id, formData);
                toast.success('Moneda actualizada exitosamente');
            } else {
                await createMoneda(token, formData);
                toast.success('Moneda creada exitosamente');
            }
            setTimeout(() => navigate('/parametrizacion/monedas'), 1000);
        } catch (error) {
            console.error('Error al guardar:', error);
            toast.error(error.response?.data?.message || 'Error al guardar la moneda');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto bg-white shadow-sm border border-gray-200 mb-6 font-sans">
            <Toaster position="top-right" />

            {/* Header */}
            <div className="bg-slate-700 p-4 flex items-center gap-3 text-white border-b border-white/10">
                <button
                    onClick={() => navigate('/parametrizacion/monedas')}
                    className="p-1.5 hover:bg-slate-600 rounded-sm transition-colors text-slate-300 hover:text-white"
                    title="Volver a la lista"
                >
                    <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-2">
                    <Coins className="w-5 h-5 text-blue-400" />
                    <div>
                        <h1 className="text-lg font-bold tracking-tight">
                            {isEditMode ? 'Gestión de Divisas: Editar Moneda' : 'Gestión de Divisas: Nueva Moneda'}
                        </h1>
                        <p className="text-[0.7rem] text-slate-400 uppercase tracking-widest font-medium">Configuración del sistema</p>
                    </div>
                </div>
            </div>

            {/* Form */}
            <div className="p-8">
                <form onSubmit={handleSubmit} className="space-y-8">

                    <div className="flex flex-col md:flex-row gap-8">
                        {/* Acentuated Card for Info */}
                        <div className="md:w-1/3 bg-slate-50 p-6 border-l-4 border-blue-500 rounded-r-md">
                            <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">Instrucciones</h3>
                            <p className="text-sm text-slate-500 leading-relaxed">
                                Use nombres descriptivos (ej. "GUARANIES").
                                <br /><br />
                                El <strong>Código ISO</strong> debe seguir el estándar internacional (ej. PYG, USD).
                                <br /><br />
                                El <strong>Símbolo</strong> se usará para mostrar montos en reportes.
                            </p>
                        </div>

                        {/* Input Fields */}
                        <div className="md:w-2/3 space-y-6">
                            <div>
                                <label className="block text-xs font-bold text-gray-600 mb-2 uppercase tracking-widest">
                                    Nombre de la Divisa <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    required
                                    maxLength="50"
                                    className="w-full px-4 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all placeholder:text-gray-300 font-medium"
                                    placeholder="Ej: GUARANIES"
                                    value={formData.nombre}
                                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value.toUpperCase() })}
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-2 uppercase tracking-widest">
                                        Código ISO <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        maxLength="5"
                                        className="w-full px-4 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all font-mono font-bold text-slate-700"
                                        placeholder="PYG"
                                        value={formData.codigoIso}
                                        onChange={(e) => setFormData({ ...formData, codigoIso: e.target.value.toUpperCase() })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-2 uppercase tracking-widest">
                                        Símbolo <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        maxLength="5"
                                        className="w-full px-4 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all font-bold text-slate-700"
                                        placeholder="Gs."
                                        value={formData.simbolo}
                                        onChange={(e) => setFormData({ ...formData, simbolo: e.target.value })}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="pt-6 border-t border-slate-100 flex justify-end items-center space-x-4">
                        <button
                            type="button"
                            onClick={() => navigate('/parametrizacion/monedas')}
                            className="px-6 py-2 border border-gray-300 text-gray-600 rounded-sm hover:bg-gray-50 transition-colors flex items-center text-xs font-bold tracking-widest"
                        >
                            <X className="w-4 h-4 mr-2" />
                            CANCELAR
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-8 py-2 bg-blue-600 text-white rounded-sm hover:bg-blue-700 transition-all flex items-center text-xs font-bold tracking-widest disabled:opacity-50 shadow-md shadow-blue-200"
                        >
                            {loading ? (
                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Save className="w-4 h-4 mr-2" />
                            )}
                            {isEditMode ? 'CONFIRMAR CAMBIOS' : 'GUARDAR REGISTRO'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}